import React from 'react';
import { connectableAccounts, vaultSignInOptions } from '../GitConnectModal';
import CompactSelectPicker from '../ui/CompactSelectPicker';
import { hostOf } from '../../services/git/gitAccounts';
import { useVaultStore } from '../../store/vaultStore';
import VaultUnlockInline from './VaultUnlockInline';

/**
 * Where "Clone a repository" can clone from: the same list Connect offers —
 * GitHub, every account added on the Git page (GitLab, Gitea …) and "Other git
 * server" with a Vault sign-in. Clone used to be GitHub-only, so a teammate or
 * a second device on GitLab or a company server could not get a workspace at
 * all (Windows Sandbox, card 06010f63, 03.10.2026). GitHub stays first and
 * keeps working signed out (public repositories).
 */
export async function cloneSources() {
  const list = await connectableAccounts();
  if (!list.some((s) => s.provider === 'github')) {
    list.unshift({ key: 'github', provider: 'github', accountId: null, label: 'GitHub', login: '' });
  }
  return list;
}

/** Vault items that can sign in to git: Password (HTTPS token) and SFTP (SSH key). Names only. */
const GIT_CAPABLE = new Set(['password', 'sftp']);

/**
 * The fields for a non-GitHub source. GitHub keeps its own picker in
 * WorkspaceModal (your repositories, or a pasted URL).
 */
export default function CloneSourceFields({ source, server, repoInput, onRepoInput, vaultItemId, onVaultItem, disabled }) {
  const vaultItems = useVaultStore((s) => s.items);
  const vaultUnlocked = useVaultStore((s) => Boolean(s.status?.unlocked));
  const credentials = (vaultItems || []).filter((it) => GIT_CAPABLE.has(it.type));

  if (source?.provider === 'remote') {
    return (
      <>
        <div>
          <label htmlFor="clone-remote-url" className="modal-field-label text-sm font-medium text-gray-700 dark:text-zinc-300">
            Repository URL
          </label>
          <input
            id="clone-remote-url"
            type="text"
            value={repoInput}
            onChange={(e) => onRepoInput(e.target.value)}
            disabled={disabled}
            placeholder="https://git.example.com/team/api.git or ssh://git@host:22/team/api.git"
            className="modal-input"
          />
        </div>
        <div>
          <span className="modal-field-label text-sm font-medium text-gray-700 dark:text-zinc-300">
            Sign in with (from your Vault)
          </span>
          {!vaultUnlocked ? (
            <VaultUnlockInline disabled={disabled} />
          ) : (
            <div className="modal-picker" style={{ display: 'flex', width: '100%' }}>
              <CompactSelectPicker
                value={vaultItemId}
                onChange={onVaultItem}
                options={vaultSignInOptions(credentials)}
                placeholder="Choose a Password or SSH key item…"
                disabled={disabled}
                aria-label="Sign in with (from your Vault)"
              />
            </div>
          )}
          <p className="mt-1 text-xs text-gray-500">The secret stays in the Vault; Apiboo reads it only to talk to this server.</p>
        </div>
      </>
    );
  }

  const host = server ? hostOf(server) : 'gitlab.com';
  const shape = source?.provider === 'gitlab' || !source?.provider ? 'group/project' : 'owner/repository';
  return (
    <div>
      <label htmlFor="clone-account-repo" className="modal-field-label text-sm font-medium text-gray-700 dark:text-zinc-300">
        Repository
      </label>
      <input
        id="clone-account-repo"
        type="text"
        value={repoInput}
        onChange={(e) => onRepoInput(e.target.value)}
        disabled={disabled}
        placeholder={`${shape} or a ${host} URL`}
        className="modal-input"
      />
      <p className="mt-1 text-xs text-gray-500">Signed in as {source?.login} on {host} — private repositories work too.</p>
    </div>
  );
}

/** Vault item for an id, names only — what the git link remembers. */
export function vaultItemFor(id) {
  return (useVaultStore.getState().items || []).find((it) => it.id === id && GIT_CAPABLE.has(it.type)) || null;
}
